import uniq from 'lodash/uniq'

import { PillarsMenu } from '@/models/channels'
import { hiddenPillars } from '@/router/routes/pillars/metaComponents'

import { setStoragePillars } from './storage'

const setPillarActiveTab = (
  state,
  { pillar, tab, updateStorage, isOpen = true }
) => {
  state[pillar].activeTab = tab
  state[pillar].isOpen = isOpen
  if (updateStorage) setStoragePillars({ [pillar]: state[pillar] })
}

const updatePillar = (state, { pillar, tabs, tab }) => {
  if (tabs) {
    Object.assign(state[pillar], { tabs: uniq(tabs) })
  }

  const pillarHasTab = state[pillar].tabs.includes(tab)

  if (pillarHasTab && tab) {
    setPillarActiveTab(state, { pillar, tab })
  } else {
    setPillarActiveTab(state, {
      pillar,
      tab: [].concat(state[pillar].tabs).pop(),
      isOpen: false
    })
  }
}

const togglePillar = (state, { id: pillarId, isOpen }) => {
  state[pillarId].isOpen = !isOpen
  setStoragePillars({ [pillarId]: state[pillarId] })
}

const setTabsByStorage = (state, storagePillars) => {
  const { firstPillar, secondPillar, thirdPillar } = storagePillars

  Object.assign(state.firstPillar, firstPillar)
  Object.assign(state.secondPillar, secondPillar)
  Object.assign(state.thirdPillar, thirdPillar)
}

const setTabsByPreset = (state, tabsPreset) => {
  const { firstPillar, secondPillar, thirdPillar } = tabsPreset
  updatePillar(state, {
    pillar: 'firstPillar',
    tabs: firstPillar,
    tab: firstPillar[0]
  })
  updatePillar(state, {
    pillar: 'secondPillar',
    tabs: secondPillar,
    tab: secondPillar[0]
  })
  updatePillar(state, {
    pillar: 'thirdPillar',
    tabs: thirdPillar,
    tab: thirdPillar[0]
  })

  setStoragePillars({
    firstPillar: state.firstPillar,
    secondPillar: state.secondPillar,
    thirdPillar: state.thirdPillar
  })
}

/**
 * Add a tab to a specific pillar by pillar position
 * ps.: this method was updated to support new appointment UX
 * where the timeline pillar cannot be duplicated, only moved
 * between pillars
 * @param {*} state
 * @param {*} {
 *  pillar: 'firstPillar', 'secondPillar', 'thirdPillar',
 *  tab: 'tab_name'
 * }
 */
const addTabToPillar = (state, { pillar, tab }) => {
  //Don`t let hidden tabs open in more than one pillar
  if (hiddenPillars.includes(tab)) {
    const pillars = ['firstPillar', 'secondPillar', 'thirdPillar']
    const duplicatedTab = pillars.reduce((acc, cur) => {
      if (state[cur]?.tabs.some(value => hiddenPillars.includes(value))) {
        acc = {
          value: true,
          pillar: cur
        }
      }
      return acc
    }, {})

    if (duplicatedTab.value) {
      removeTabFromPillar(state, { pillar: duplicatedTab.pillar, tab })
    }
  }

  if (state[pillar].tabs.includes(tab)) return
  state[pillar].tabs.push(tab)
  updatePillar(state, { pillar, tab })
  setStoragePillars({ [pillar]: state[pillar] })
}

const removeTabFromPillar = (state, { pillar, tab }) => {
  const index = state[pillar].tabs.findIndex(item => item === tab)
  state[pillar].tabs.splice(index, 1)
  if (state[pillar].activeTab === tab) updatePillar(state, { pillar })
  setStoragePillars({ [pillar]: state[pillar] })
}

/**
 * Remove tab from pillars without knowing in which pillar it is being instanced
 * Use case: when a component needs to remove a tab from pillars
 * @param {*} state
 */
const removeHiddenTabFromPillars = (state, tabId) => {
  const pillars = ['firstPillar', 'secondPillar', 'thirdPillar']
  const pillar = pillars.reduce((acc, cur) => {
    const i = state[cur]?.tabs.findIndex(tab => tab === tabId)
    if (i !== -1) {
      acc = {
        value: cur,
        tabIndex: i
      }
    }
    return acc
  }, {})

  if (!pillar.value) return

  state[pillar.value].tabs.splice(pillar.tabIndex, 1)
  if (state[pillar.value].activeTab === tabId) {
    const tabs = state[pillar.value].tabs
    updatePillar(state, {
      pillar: pillar.value,
      tab: tabs[tabs.length - 1]
    })
  }
  setStoragePillars({ [pillar.value]: state[pillar.value] })
}

const handleChannelTab = state => {
  const tab = 'channel'
  const pillarWithChannels = [state.firstPillar, state.secondPillar].find(
    pillar => pillar.tabs.includes(tab)
  )

  if (pillarWithChannels) {
    updatePillar(state, {
      pillar: pillarWithChannels.id,
      tabs: pillarWithChannels.tabs,
      tab
    })
  } else {
    addTabToPillar(state, { pillar: 'firstPillar', tab })
  }
}

const openPillarFromTab = (
  state,
  {
    actualPillarId,
    targetTab,
    isCloseUnrelatedPillar = false,
    notChannelPillar
  }
) => {
  const originPillarId = actualPillarId

  const tabFallback = notChannelPillar?.id || 'secondPillar'

  const nextPillarFromOrigin =
    {
      firstPillar: 'secondPillar',
      secondPillar: 'thirdPillar',
      thirdPillar: 'secondPillar'
    }[originPillarId] || tabFallback

  addTabToPillar(state, { pillar: nextPillarFromOrigin, tab: targetTab })

  if (isCloseUnrelatedPillar) {
    const relatedPillars = [actualPillarId, nextPillarFromOrigin]
    closeUnrelatedPillar(state, relatedPillars)
  }
}

const closeUnrelatedPillar = (state, relatedPillarIds) => {
  const unrelatedPillar = ['firstPillar', 'secondPillar', 'thirdPillar'].find(
    p => !relatedPillarIds.includes(p)
  )
  togglePillar(state, { id: unrelatedPillar, isOpen: true })
}

const setPersonId = (state, { personId }) => {
  Object.assign(state, { personId })
}

const handleOpenPillars = (state, menuState) => {
  const openedPillars = [
    state.firstPillar,
    state.secondPillar,
    state.thirdPillar
  ].filter(pillar => pillar.isOpen)
  const isCompacted = menuState === PillarsMenu.COMPACT
  const isExpanded = menuState === PillarsMenu.EXPANDED

  if (
    (openedPillars.length === 3 && isCompacted) ||
    (openedPillars.length === 2 && isExpanded)
  ) {
    const lastVisiblePillar = openedPillars.pop()
    state[lastVisiblePillar.id].isOpen = false
  }
}

const resetState = (state, modules) => {
  Object.keys(modules).forEach(key => {
    let initialState = { ...modules[key].state }
    if (typeof modules[key].state === 'function') {
      initialState = modules[key].state()
    }
    Object.assign(state[key], initialState)
    if (modules[key].modules) {
      //If module has sub modules call resetState again with module state and submodules
      resetState(state[key], modules[key].modules)
    }
  })
}

const updateDraftClinicalRecordId = (state, draftClinicalRecordId) => {
  Object.assign(state, { draftClinicalRecordId })
}

export default {
  setPillarActiveTab,
  togglePillar,
  setTabsByPreset,
  addTabToPillar,
  removeTabFromPillar,
  removeHiddenTabFromPillars,
  handleChannelTab,
  setPersonId,
  openPillarFromTab,
  handleOpenPillars,
  setTabsByStorage,
  resetState,
  closeUnrelatedPillar,
  updateDraftClinicalRecordId
}
